import { useCallback, useMemo } from "react";
import { Twitter, Linkedin, MessageCircle, Facebook, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { buildShareUrl } from "@/utils/utm";

/**
 * Share buttons for entity detail pages. Each social channel adds its own
 * utm_source / utm_medium / utm_campaign so GA4 can attribute traffic per
 * network. `sponsorSlug` populates utm_content for sponsorship attribution.
 */
export default function ShareButtons({ title, url, type, slug, sponsorSlug }) {
  const enc = (s) => encodeURIComponent(s);

  const urls = useMemo(() => {
    const forChannel = (channel) =>
      type && slug ? buildShareUrl(url, { type, slug, channel, sponsorSlug }) : url;
    return {
      twitter: forChannel("twitter"),
      linkedin: forChannel("linkedin"),
      whatsapp: forChannel("whatsapp"),
      facebook: forChannel("facebook"),
      copy: forChannel("copy_link"),
    };
  }, [url, type, slug, sponsorSlug]);

  const items = [
    { label: "Twitter", icon: Twitter, href: `https://twitter.com/intent/tweet?text=${enc(title)}&url=${enc(urls.twitter)}` },
    { label: "LinkedIn", icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(urls.linkedin)}` },
    { label: "WhatsApp", icon: MessageCircle, href: `https://wa.me/?text=${enc(`${title} — ${urls.whatsapp}`)}` },
    { label: "Facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${enc(urls.facebook)}` },
  ];

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(urls.copy);
      toast.success("Link copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  }, [urls.copy]);

  return (
    <div data-testid="share-buttons" className="flex items-center gap-2 flex-wrap">
      <span className="text-[11px] uppercase tracking-[0.22em] text-charcoal/60 mr-1">
        Compartir
      </span>
      {items.map(({ label, icon: Icon, href }) => (
        <a
          key={label}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`share-${label.toLowerCase()}`}
          aria-label={`Compartir en ${label}`}
          className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-charcoal/20 text-charcoal hover:bg-charcoal hover:text-cream transition-colors duration-[400ms]"
        >
          <Icon className="h-4 w-4" />
        </a>
      ))}
      <button
        type="button"
        onClick={copy}
        data-testid="share-copy"
        aria-label="Copiar link"
        className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-charcoal/20 text-charcoal hover:bg-charcoal hover:text-cream transition-colors duration-[400ms]"
      >
        <LinkIcon className="h-4 w-4" />
      </button>
    </div>
  );
}
